import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Download, Shuffle, LogIn, LogOut, Users, Trophy, X, Star } from 'lucide-react';
import confetti from 'canvas-confetti';
import logoImg from '../assets/LOGO.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---- Wheel Segments: 20 total ----
function buildSegments() {
  const values = [
    ...Array(4).fill(5),
    ...Array(4).fill(10),
    ...Array(3).fill(15),
    ...Array(3).fill(20),
    ...Array(2).fill(25),
    ...Array(1).fill(30),
    ...Array(1).fill(50),
    ...Array(1).fill(100),
  ]; // 19 + 1 jackpot = 20
  for (let i = values.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [values[i], values[j]] = [values[j], values[i]];
  }
  const jackpotIdx = Math.floor(Math.random() * 20);
  values.splice(jackpotIdx, 0, 500);
  return values.slice(0, 20).map((v, i) => ({ value: v, special: v === 500, idx: i }));
}

const SEG_ANGLE = 18; // 360 / 20

function segColor(seg) {
  if (seg.special) return { fill: '#D4AF37', text: '#05080F', glow: true };
  return seg.idx % 2 === 0
    ? { fill: '#070D1E', text: '#D4AF37', glow: false }
    : { fill: '#0D1530', text: '#C9A84C', glow: false };
}

// ---- Canvas Wheel ----
function LuckyWheel({ rotation, spinning, segments }) {
  const canvasRef = useRef(null);

  const draw = useCallback((rot) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const cx = W / 2, cy = H / 2;
    // Reserve space for outer band
    const R = Math.min(W, H) / 2 - 20;
    const BAND = 16;

    ctx.clearRect(0, 0, W, H);

    // Dark background circle
    ctx.beginPath();
    ctx.arc(cx, cy, R + BAND + 4, 0, Math.PI * 2);
    ctx.fillStyle = '#030509';
    ctx.fill();

    const startRad = (rot - 90) * Math.PI / 180;

    // === DRAW 100 SEGMENTS ===
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      const a1 = startRad + (i * SEG_ANGLE * Math.PI / 180);
      const a2 = a1 + (SEG_ANGLE * Math.PI / 180);
      const midAngle = a1 + (SEG_ANGLE * Math.PI / 180) / 2;

      ctx.save();
      ctx.shadowBlur = 0;

      // Fill segment
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, R, a1, a2);
      ctx.closePath();

      if (seg.special) {
        // Jackpot: golden gradient
        const g = ctx.createLinearGradient(
          cx + Math.cos(midAngle) * R * 0.3, cy + Math.sin(midAngle) * R * 0.3,
          cx + Math.cos(midAngle) * R, cy + Math.sin(midAngle) * R
        );
        g.addColorStop(0, '#8B6914');
        g.addColorStop(0.5, '#D4AF37');
        g.addColorStop(1, '#FFE566');
        ctx.fillStyle = g;
        ctx.shadowColor = '#FFD700';
        ctx.shadowBlur = 20;
      } else {
        // Alternating: royal blue vs deep red — strongly contrasting
        ctx.fillStyle = i % 2 === 0 ? '#1B3E7A' : '#7A1B1B';
      }
      ctx.fill();
      ctx.restore();

      // Gold divider lines — draw as two radial lines per segment boundary
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(a1) * R, cy + Math.sin(a1) * R);
      ctx.strokeStyle = i % 5 === 0 ? 'rgba(212,175,55,0.85)' : 'rgba(212,175,55,0.35)';
      ctx.lineWidth = i % 5 === 0 ? 1.5 : 0.8;
      ctx.stroke();
      ctx.restore();

      // Number text
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(midAngle);
      ctx.textAlign = 'right';
      if (seg.special) {
        ctx.fillStyle = '#1A0A00';
        ctx.font = 'bold 14px Montserrat,sans-serif';
        ctx.shadowColor = '#FFD700'; ctx.shadowBlur = 3;
        ctx.fillText('★500', R - 12, 4);
      } else {
        ctx.fillStyle = i % 2 === 0 ? '#A8C8FF' : '#FFA8A8';
        ctx.font = '12px Montserrat,sans-serif';
        ctx.shadowBlur = 0;
        ctx.fillText(String(seg.value), R - 12, 4);
      }
      ctx.restore();
    }

    // === OUTER LUXURY BAND ===
    // Dark band ring
    ctx.beginPath();
    ctx.arc(cx, cy, R + BAND, 0, Math.PI * 2);
    ctx.arc(cx, cy, R, 0, Math.PI * 2, true);
    ctx.fillStyle = '#0A0810';
    ctx.fill();

    // 20 dots in band (one per segment)
    for (let i = 0; i < 20; i++) {
      const angle = startRad + (i * SEG_ANGLE * Math.PI / 180);
      const dotR = R + BAND / 2;
      const big = i % 10 === 0;
      ctx.beginPath();
      ctx.arc(cx + Math.cos(angle) * dotR, cy + Math.sin(angle) * dotR, big ? 3.5 : 1.8, 0, Math.PI * 2);
      ctx.fillStyle = big ? '#FFD700' : 'rgba(212,175,55,0.55)';
      if (big) { ctx.shadowColor = '#FFD700'; ctx.shadowBlur = 10; }
      ctx.fill();
      ctx.shadowBlur = 0;
    }

    // 4 diamond markers (every 5 segments)
    for (let d = 0; d < 4; d++) {
      const angle = startRad + (d * 90 * Math.PI / 180);
      const dr = R + BAND / 2;
      ctx.save();
      ctx.translate(cx + Math.cos(angle) * dr, cy + Math.sin(angle) * dr);
      ctx.rotate(angle + Math.PI / 4);
      ctx.fillStyle = '#FFD700';
      ctx.shadowColor = '#FFD700'; ctx.shadowBlur = 14;
      ctx.fillRect(-5.5, -5.5, 11, 11);
      ctx.shadowBlur = 0;
      ctx.restore();
    }

    // Gold border rings
    ctx.beginPath(); ctx.arc(cx, cy, R + BAND, 0, Math.PI * 2);
    ctx.strokeStyle = '#D4AF37'; ctx.lineWidth = 3;
    ctx.shadowColor = 'rgba(212,175,55,0.6)'; ctx.shadowBlur = 10;
    ctx.stroke(); ctx.shadowBlur = 0;

    ctx.beginPath(); ctx.arc(cx, cy, R + BAND / 2, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(212,175,55,0.3)'; ctx.lineWidth = 1;
    ctx.stroke();

    ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(212,175,55,0.6)'; ctx.lineWidth = 1.5;
    ctx.stroke();

    // Inner decorative ring at 85%
    ctx.beginPath(); ctx.arc(cx, cy, R * 0.85, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(212,175,55,0.25)'; ctx.lineWidth = 1;
    ctx.stroke();

    // === ORNATE HUB ===
    // Glow halo
    ctx.beginPath(); ctx.arc(cx, cy, R * 0.26, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(212,175,55,0.05)';
    ctx.shadowColor = '#D4AF37'; ctx.shadowBlur = 24;
    ctx.fill(); ctx.shadowBlur = 0;

    ctx.beginPath(); ctx.arc(cx, cy, R * 0.22, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(212,175,55,0.4)'; ctx.lineWidth = 1; ctx.stroke();

    // Hub fill
    const hubG = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 0.2);
    hubG.addColorStop(0, '#1A1E2A'); hubG.addColorStop(1, '#060810');
    ctx.beginPath(); ctx.arc(cx, cy, R * 0.2, 0, Math.PI * 2);
    ctx.fillStyle = hubG;
    ctx.shadowColor = '#D4AF37'; ctx.shadowBlur = 18;
    ctx.fill(); ctx.shadowBlur = 0;

    ctx.beginPath(); ctx.arc(cx, cy, R * 0.13, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(212,175,55,0.6)'; ctx.lineWidth = 1.5; ctx.stroke();

    ctx.beginPath(); ctx.arc(cx, cy, R * 0.2, 0, Math.PI * 2);
    ctx.strokeStyle = '#D4AF37'; ctx.lineWidth = 2.5;
    ctx.shadowColor = '#FFD700'; ctx.shadowBlur = 10;
    ctx.stroke(); ctx.shadowBlur = 0;

    // 8 dots around hub
    for (let d = 0; d < 8; d++) {
      const a = (d * 45 * Math.PI / 180);
      ctx.beginPath();
      ctx.arc(cx + Math.cos(a) * R * 0.22, cy + Math.sin(a) * R * 0.22, 3, 0, Math.PI * 2);
      ctx.fillStyle = '#D4AF37'; ctx.shadowColor = '#FFD700'; ctx.shadowBlur = 6;
      ctx.fill(); ctx.shadowBlur = 0;
    }

    // Center symbol
    ctx.fillStyle = '#FFD700';
    ctx.font = `bold ${Math.floor(R * 0.12)}px serif`;
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.shadowColor = '#FFD700'; ctx.shadowBlur = 16;
    ctx.fillText('✦', cx, cy);
    ctx.shadowBlur = 0;

    // Outer glow effect (LAST — on top of everything)
    const outerGlow = ctx.createRadialGradient(cx, cy, R + BAND - 4, cx, cy, R + BAND + 4);
    outerGlow.addColorStop(0, 'rgba(212,175,55,0.4)');
    outerGlow.addColorStop(1, 'rgba(212,175,55,0)');
    ctx.beginPath(); ctx.arc(cx, cy, R + BAND + 5, 0, Math.PI * 2);
    ctx.fillStyle = outerGlow;
    ctx.fill();

  }, [segments]);

  useEffect(() => { draw(rotation); }, [rotation, draw]);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div style={{ position: 'absolute', top: -18, left: '50%', transform: 'translateX(-50%)', zIndex: 10, filter: 'drop-shadow(0 0 6px rgba(212,175,55,0.8))' }}>
        <svg width="28" height="28" viewBox="0 0 28 28">
          <polygon points="14,28 2,2 26,2" fill="#D4AF37" />
          <polygon points="14,28 2,2 26,2" fill="none" stroke="#FFD700" strokeWidth="1" />
        </svg>
      </div>
      <canvas ref={canvasRef} width={580} height={580}
        style={{ borderRadius: '50%', display: 'block', maxWidth: '92vw', maxHeight: '92vw' }}
        data-testid="lucky-wheel-canvas" />
    </div>
  );
}

// ---- Mystical Golden Fog Canvas ----
function GoldenFog() {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    // Init particles — faster movement
    const N = 32;
    particlesRef.current = Array.from({ length: N }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 160 + 70,
      vx: (Math.random() - 0.5) * 0.6,
      vy: -(Math.random() * 0.9 + 0.35),
      maxOp: Math.random() * 0.13 + 0.05,
      phase: Math.random() * Math.PI * 2,
    }));

    const ctx = canvas.getContext('2d');
    let t = 0;
    const loop = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      t += 0.022;
      particlesRef.current.forEach(p => {
        p.x += p.vx + Math.sin(t + p.phase) * 0.4;
        p.y += p.vy;
        if (p.y + p.size < 0) {
          p.y = canvas.height + p.size;
          p.x = Math.random() * canvas.width;
        }
        const op = p.maxOp * (0.5 + 0.5 * Math.sin(t * 1.4 + p.phase));
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size);
        g.addColorStop(0, `rgba(212,175,55,${op})`);
        g.addColorStop(0.45, `rgba(180,130,30,${op * 0.5})`);
        g.addColorStop(1, 'rgba(212,175,55,0)');
        ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = g; ctx.fill();
      });
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener('resize', resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <canvas ref={canvasRef} style={{
      position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
    }} aria-hidden="true" />
  );
}

// ---- Casino Roulette Spin Sound ----
function createSpinSound(durationMs) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const duration = durationMs / 1000;
    const master = ctx.createGain();
    master.gain.value = 1.5;
    master.connect(ctx.destination);

    const easeOut = t => 1 - Math.pow(1 - t, 4);

    // Schedule sharp roulette ball clicks at decreasing intervals
    let time = 0;
    let interval = 0.028; // Start: ~36 clicks/sec (very fast)
    const maxInterval = 0.65; // End: ~1.5 clicks/sec (last few clicks)

    while (time < duration - 0.15) {
      ((t, progress) => {
        // Sharp click: short noise burst + bandpass for wooden/ball sound
        const sLen = Math.floor(ctx.sampleRate * 0.012);
        const buf = ctx.createBuffer(1, sLen, ctx.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < sLen; i++) {
          const env = Math.pow(1 - i / sLen, 5);
          data[i] = (Math.random() * 2 - 1) * env;
        }
        const src = ctx.createBufferSource();
        src.buffer = buf;

        // Bandpass: higher freq when fast, lower when slow — like a real ball
        const bp = ctx.createBiquadFilter();
        bp.type = 'bandpass';
        bp.frequency.value = 2200 - progress * 900; // 2200Hz → 1300Hz
        bp.Q.value = 5;

        // Slight pitch variation per click
        const tone = ctx.createOscillator();
        const toneGain = ctx.createGain();
        tone.type = 'sine';
        tone.frequency.value = 3200 - progress * 1200;
        toneGain.gain.setValueAtTime(0.18, ctx.currentTime + t);
        toneGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.01);
        tone.connect(toneGain);
        toneGain.connect(master);
        tone.start(ctx.currentTime + t);
        tone.stop(ctx.currentTime + t + 0.015);

        const g = ctx.createGain();
        // Louder on the slow final clicks for drama
        g.gain.value = progress > 0.85 ? 1.1 : 0.75;

        src.connect(bp); bp.connect(g); g.connect(master);
        src.start(ctx.currentTime + t);
      })(time, easeOut(Math.min(time / duration, 1)));

      const progress = easeOut(Math.min(time / duration, 1));
      interval = 0.028 + progress * maxInterval;
      time += interval;
    }

    return ctx;
  } catch (e) { return null; }
}


function playWinSound(isJackpot) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const master = ctx.createGain();
    master.gain.value = 0.55;
    master.connect(ctx.destination);

    // Add reverb-like delay
    const delay = ctx.createDelay(0.3);
    const delayGain = ctx.createGain();
    delay.delayTime.value = 0.18;
    delayGain.gain.value = 0.25;
    delay.connect(delayGain);
    delayGain.connect(master);

    const playNote = (freq, startTime, duration, type = 'sine', vol = 0.35) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(vol, startTime + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
      osc.connect(gain);
      gain.connect(master);
      gain.connect(delay);
      osc.start(startTime);
      osc.stop(startTime + duration + 0.1);
    };

    if (isJackpot) {
      // Epic jackpot fanfare
      const seq = [261.63, 329.63, 392.00, 523.25, 659.25, 783.99, 1046.50];
      seq.forEach((f, i) => playNote(f, ctx.currentTime + i * 0.11, 0.7, 'sine', 0.4));
      // Chord sustain
      [523.25, 659.25, 783.99].forEach(f => playNote(f, ctx.currentTime + 0.85, 1.8, 'triangle', 0.28));
      // Sparkle hits
      [2093, 1568, 2637].forEach((f, i) => playNote(f, ctx.currentTime + 0.9 + i * 0.15, 0.4, 'sine', 0.15));
    } else {
      // Warm fanfare for regular win
      const seq = [261.63, 329.63, 392.00, 523.25];
      seq.forEach((f, i) => playNote(f, ctx.currentTime + i * 0.1, 0.55, 'sine', 0.38));
      // Chord
      [523.25, 659.25].forEach(f => playNote(f, ctx.currentTime + 0.45, 1.2, 'triangle', 0.25));
      // Sparkle
      playNote(2093, ctx.currentTime + 0.5, 0.35, 'sine', 0.12);
    }

    // Drum hit (low thud)
    const bufferSize = ctx.sampleRate * 0.15;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 3);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const drumGain = ctx.createGain();
    drumGain.gain.value = isJackpot ? 0.6 : 0.4;
    src.connect(drumGain);
    drumGain.connect(master);
    src.start(ctx.currentTime);
  } catch (e) {
    console.warn('Audio not available:', e);
  }
}

// ---- Fireworks: dedicated canvas above ALL layers ----
let fireworksCanvas = null;
let fireworksInstance = null;

function getFireworksInstance() {
  if (!fireworksCanvas) {
    fireworksCanvas = document.createElement('canvas');
    fireworksCanvas.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:99999;';
    document.body.appendChild(fireworksCanvas);
    fireworksInstance = confetti.create(fireworksCanvas, { resize: true, useWorker: false });
  }
  return fireworksInstance;
}

function launchFireworks(isJackpot) {
  const fire = getFireworksInstance();
  if (fireworksCanvas) {
    fireworksCanvas.width = window.innerWidth;
    fireworksCanvas.height = window.innerHeight;
  }

  const duration = isJackpot ? 8000 : 5000;
  const end = Date.now() + duration;

  const goldColors   = ['#FFD700', '#FFF8DC', '#D4AF37', '#FFFACD', '#F5C518'];
  const festiveColors = ['#FF3366', '#FF6B35', '#FFD700', '#00E5FF', '#A8FF3E', '#FF00FF', '#FFFFFF'];
  const colors = isJackpot ? festiveColors : goldColors;

  // Burst from center on reveal
  fire({ particleCount: isJackpot ? 120 : 60, spread: 100, startVelocity: 55, origin: { x: 0.5, y: 0.5 }, colors, scalar: 1.1, ticks: 200 });

  const frame = () => {
    if (Date.now() > end) return;
    const remaining = (end - Date.now()) / duration;
    const burst = Math.max(1, Math.floor(remaining * (isJackpot ? 18 : 10)));

    // Left cannon
    fire({ particleCount: burst, angle: 55, spread: 70, startVelocity: 60, origin: { x: 0, y: 0.65 }, colors, scalar: 1.2, ticks: 180, gravity: 0.9 });
    // Right cannon
    fire({ particleCount: burst, angle: 125, spread: 70, startVelocity: 60, origin: { x: 1, y: 0.65 }, colors, scalar: 1.2, ticks: 180, gravity: 0.9 });

    if (isJackpot) {
      // Extra top bursts
      fire({ particleCount: Math.ceil(burst * 0.7), angle: 90, spread: 160, startVelocity: 65, origin: { x: 0.5, y: 0.3 }, colors, scalar: 1.3, ticks: 220, gravity: 0.7 });
      // Random bursts across the screen
      if (Math.random() < 0.4) {
        fire({ particleCount: 20, spread: 360, startVelocity: 45, origin: { x: Math.random(), y: Math.random() * 0.6 }, colors, scalar: 0.9, ticks: 150 });
      }
    }
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);

  // Play sound
  playWinSound(isJackpot);
}

// ---- Styles ----
const btnStyle = (bg, color, extra = {}) => ({
  display: 'inline-flex', alignItems: 'center', gap: '7px',
  padding: '9px 16px', background: bg,
  border: `1px solid ${color === '#05080F' ? 'transparent' : color + '55'}`,
  borderRadius: '10px', color, fontFamily: 'Montserrat,sans-serif',
  fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
  letterSpacing: '0.05em', transition: 'opacity 0.2s', ...extra,
});
const inputStyle = {
  background: '#070B14', border: '1px solid rgba(245,230,211,0.15)',
  borderRadius: '10px', color: '#F5E6D3', fontFamily: 'Montserrat,sans-serif',
  fontSize: '0.9rem', padding: '11px 14px', outline: 'none', width: '100%',
  boxSizing: 'border-box',
};
const tdStyle = { padding: '8px 10px', color: '#F5E6D3', fontSize: '0.82rem' };

// ---- Admin Dashboard ----
function AdminDashboard({ onClose }) {
  const { t } = useTranslation();
  const [token, setToken] = useState(sessionStorage.getItem('gw_admin_token') || '');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [gameActive, setGameActive] = useState(false);
  const [players, setPlayers] = useState([]);
  const [randomDraw, setRandomDraw] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('players');
  const [emailSending, setEmailSending] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');

  const fetchStatus = useCallback(async () => {
    try { const r = await fetch(`${BACKEND_URL}/api/gewinnspiel/status`); const d = await r.json(); setGameActive(d.active); } catch {}
  }, []);

  const fetchPlayers = useCallback(async (tok) => {
    try { const r = await fetch(`${BACKEND_URL}/api/gewinnspiel/admin/players`, { headers: { Authorization: `Bearer ${tok}` } }); const d = await r.json(); setPlayers(d.players || []); } catch {}
  }, []);

  const fetchClaims = useCallback(async (tok) => {
    try { const r = await fetch(`${BACKEND_URL}/api/gewinnspiel/admin/claims`, { headers: { Authorization: `Bearer ${tok}` } }); const d = await r.json(); setClaims(d.claims || []); } catch {}
  }, []);

  useEffect(() => { fetchStatus(); if (token) { fetchPlayers(token); fetchClaims(token); } }, [token, fetchStatus, fetchPlayers, fetchClaims]);

  const doLogin = async () => {
    setLoginError(''); setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/gewinnspiel/admin/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(loginForm) });
      if (!r.ok) { setLoginError('Ungültige Anmeldedaten'); return; }
      const d = await r.json();
      setToken(d.token); sessionStorage.setItem('gw_admin_token', d.token);
    } catch { setLoginError('Verbindungsfehler'); } finally { setLoading(false); }
  };

  const toggleGame = async () => {
    const ep = gameActive ? 'deactivate' : 'activate';
    await fetch(`${BACKEND_URL}/api/gewinnspiel/admin/${ep}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    setGameActive(!gameActive);
  };

  const downloadCSV = (endpoint, filename) => {
    fetch(`${BACKEND_URL}/api/gewinnspiel/admin/${endpoint}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); });
  };

  const doRandomDraw = async () => {
    const r = await fetch(`${BACKEND_URL}/api/gewinnspiel/admin/random-draw`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json(); setRandomDraw(d.selected || []); setTab('draw');
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(5,8,15,0.93)', backdropFilter: 'blur(12px)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ background: '#070B14', border: '1px solid rgba(212,175,55,0.3)', borderRadius: '20px', width: '100%', maxWidth: '840px', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 30px 80px rgba(0,0,0,0.8)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid rgba(212,175,55,0.15)' }}>
          <h2 style={{ fontFamily: 'Cormorant Garamond,serif', color: '#D4AF37', fontSize: '1.35rem', margin: 0 }}>Admin Dashboard — Gewinnspiel</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            {token && <button onClick={() => { setToken(''); sessionStorage.removeItem('gw_admin_token'); }} style={btnStyle('#1a0a00', '#E45D5D')}><LogOut size={13} /> Abmelden</button>}
            <button onClick={onClose} style={btnStyle('rgba(255,255,255,0.05)', '#F5E6D3')}><X size={16} /></button>
          </div>
        </div>
        <div style={{ padding: '22px' }}>
          {!token ? (
            <div style={{ maxWidth: '340px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ color: 'rgba(245,230,211,0.5)', textAlign: 'center', marginBottom: '8px' }}>Admin-Anmeldung</p>
              <input style={inputStyle} placeholder="Benutzername" value={loginForm.username} onChange={e => setLoginForm(p => ({ ...p, username: e.target.value }))} onKeyDown={e => e.key === 'Enter' && doLogin()} />
              <input style={inputStyle} type="password" placeholder="Passwort" value={loginForm.password} onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))} onKeyDown={e => e.key === 'Enter' && doLogin()} />
              {loginError && <p style={{ color: '#E45D5D', fontSize: '0.83rem', margin: 0 }}>{loginError}</p>}
              <button onClick={doLogin} disabled={loading} style={{ ...btnStyle('#D4AF37', '#05080F'), justifyContent: 'center', padding: '13px' }}><LogIn size={14} /> {loading ? '...' : 'Anmelden'}</button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px', padding: '12px 16px', background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.18)', borderRadius: '10px' }}>
                <div>
                  <p style={{ color: '#F5E6D3', fontWeight: 600, margin: 0 }}>Status: <span style={{ color: gameActive ? '#3BBE8A' : '#E45D5D' }}>{gameActive ? '● AKTIV' : '● INAKTIV'}</span></p>
                  <p style={{ color: 'rgba(245,230,211,0.45)', fontSize: '0.78rem', margin: '3px 0 0' }}>Spieler gesamt: {players.length}</p>
                </div>
                <button onClick={toggleGame} style={btnStyle(gameActive ? '#1a0000' : '#001a0a', gameActive ? '#E45D5D' : '#3BBE8A')}>{gameActive ? 'DEAKTIVIEREN' : 'AKTIVIEREN'}</button>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                {[
                  { id: 'players', icon: <Users size={13} />, label: `Spieler (${players.length})` },
                  { id: 'claims', icon: <Trophy size={13} />, label: `Claims (${claims.length})` },
                  { id: 'draw', icon: <Shuffle size={13} />, label: 'Ziehung' },
                ].map(t2 => (
                  <button key={t2.id} onClick={() => setTab(t2.id)} style={btnStyle(tab === t2.id ? 'rgba(212,175,55,0.2)' : 'rgba(255,255,255,0.04)', tab === t2.id ? '#D4AF37' : '#F5E6D3')}>{t2.icon} {t2.label}</button>
                ))}
              </div>
              {/* Send Claim Emails */}
              <div style={{ marginBottom: '16px', padding: '12px 14px', background: 'rgba(0,191,255,0.05)', border: '1px solid rgba(0,191,255,0.18)', borderRadius: '10px' }}>
                <p style={{ color: '#F5E6D3', fontWeight: 600, margin: '0 0 4px', fontSize: '0.85rem' }}>📧 Claim-Emails versenden</p>
                <p style={{ color: 'rgba(245,230,211,0.45)', fontSize: '0.75rem', margin: '0 0 10px' }}>Personalisierte E-Mail an alle {players.length} Teilnehmer mit Claim-Link senden.</p>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button onClick={async () => {
                    if (!window.confirm(`Claim-Email an alle ${players.length} Teilnehmer senden?`)) return;
                    setEmailSending(true); setEmailMsg('');
                    try {
                      const r = await fetch(`${BACKEND_URL}/api/gewinnspiel/admin/send-claim-emails`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
                      const d = await r.json(); setEmailMsg(d.message || 'Emails werden gesendet...');
                    } catch { setEmailMsg('Fehler'); } finally { setEmailSending(false); }
                  }} disabled={emailSending || players.length === 0} style={btnStyle('#0a2000', '#3BBE8A')}>
                    {emailSending ? '⏳ Wird gesendet...' : '📧 Jetzt senden'}
                  </button>
                  {emailMsg && <span style={{ color: '#3BBE8A', fontSize: '0.8rem' }}>{emailMsg}</span>}
                </div>
              </div>
              {tab === 'players' && (
                <>
                  <button onClick={() => downloadCSV('players/download', 'gewinnspiel_spieler.csv')} style={{ ...btnStyle('rgba(212,175,55,0.1)', '#D4AF37'), marginBottom: '12px' }}><Download size={13} /> CSV Herunterladen</button>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr style={{ borderBottom: '1px solid rgba(212,175,55,0.25)' }}>
                        {['Vorname','Nachname','E-Mail','Gewinn','Gespielt am'].map(h => <th key={h} style={{ padding: '7px 10px', color: '#D4AF37', textAlign: 'left', fontSize: '0.8rem' }}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {players.length === 0 && <tr><td colSpan={5} style={{ padding: '14px', color: 'rgba(245,230,211,0.35)', textAlign: 'center' }}>Noch keine Spieler</td></tr>}
                        {players.map((p, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            <td style={tdStyle}>{p.first_name}</td>
                            <td style={tdStyle}>{p.last_name}</td>
                            <td style={tdStyle}>{p.email}</td>
                            <td style={{ ...tdStyle, color: p.is_jackpot ? '#FFD700' : '#D4AF37', fontWeight: 700 }}>{p.is_jackpot ? '★ ' : ''}{p.result_value} Token</td>
                            <td style={{ ...tdStyle, color: 'rgba(245,230,211,0.4)', fontSize: '0.73rem' }}>{p.played_at ? new Date(p.played_at).toLocaleString('de-DE') : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {tab === 'claims' && (
                <>
                  <button onClick={() => downloadCSV('claims/download', 'gewinnspiel_claims.csv')} style={{ ...btnStyle('rgba(212,175,55,0.1)', '#D4AF37'), marginBottom: '12px' }}><Download size={13} /> Claims CSV</button>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr style={{ borderBottom: '1px solid rgba(212,175,55,0.25)' }}>
                        {['Vorname', 'Nachname', 'E-Mail', 'AllinX ID', 'Gewinn', 'Geclaimt am'].map(h => <th key={h} style={{ padding: '7px 10px', color: '#D4AF37', textAlign: 'left', fontSize: '0.78rem' }}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {claims.length === 0 && <tr><td colSpan={6} style={{ padding: '14px', color: 'rgba(245,230,211,0.35)', textAlign: 'center' }}>Noch keine Claims</td></tr>}
                        {claims.map((p, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            <td style={tdStyle}>{p.first_name}</td>
                            <td style={tdStyle}>{p.last_name}</td>
                            <td style={tdStyle}>{p.email}</td>
                            <td style={{ ...tdStyle, color: '#00BFFF', fontWeight: 600 }}>{p.allinx_id}</td>
                            <td style={{ ...tdStyle, color: p.is_jackpot ? '#FFD700' : '#D4AF37', fontWeight: 700 }}>{p.is_jackpot ? '★ ' : ''}{p.result_value} Token</td>
                            <td style={{ ...tdStyle, color: 'rgba(245,230,211,0.4)', fontSize: '0.73rem' }}>{p.claimed_at ? new Date(p.claimed_at).toLocaleString('de-DE') : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {tab === 'draw' && (
                <>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
                    <button onClick={doRandomDraw} style={btnStyle('#D4AF37', '#05080F')}><Shuffle size={13} /> 30 Zufällige ziehen</button>
                    {randomDraw.length > 0 && <button onClick={() => downloadCSV('random-draw/download', 'ziehung.csv')} style={btnStyle('rgba(212,175,55,0.1)', '#D4AF37')}><Download size={13} /> CSV</button>}
                  </div>
                  {randomDraw.length === 0 && <p style={{ color: 'rgba(245,230,211,0.35)', fontSize: '0.85rem' }}>Klicke auf "30 Zufällige ziehen".</p>}
                  {randomDraw.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr style={{ borderBottom: '1px solid rgba(212,175,55,0.25)' }}>
                          {['#','Vorname','Nachname','E-Mail','Gewinn'].map(h => <th key={h} style={{ padding: '7px 10px', color: '#D4AF37', textAlign: 'left', fontSize: '0.8rem' }}>{h}</th>)}
                        </tr></thead>
                        <tbody>
                          {randomDraw.map((p, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                              <td style={{ ...tdStyle, color: '#D4AF37', fontWeight: 700 }}>{i + 1}</td>
                              <td style={tdStyle}>{p.first_name}</td>
                              <td style={tdStyle}>{p.last_name}</td>
                              <td style={tdStyle}>{p.email}</td>
                              <td style={{ ...tdStyle, color: p.is_jackpot ? '#FFD700' : '#D4AF37', fontWeight: 700 }}>{p.is_jackpot ? '★ ' : ''}{p.result_value} Token</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Main Page ----
export default function GewinnspielPage() {
  const { t } = useTranslation();
  const [gameActive, setGameActive] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [segments, setSegments] = useState(() => buildSegments());
  const [musicPlaying, setMusicPlaying] = useState(false);
  const musicRef = useRef(null);

  const toggleMusic = () => {
    const audio = musicRef.current;
    if (!audio) return;
    if (musicPlaying) {
      audio.pause();
      setMusicPlaying(false);
    } else {
      audio.play().then(() => setMusicPlaying(true)).catch(() => {});
    }
  };

  // Registration
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '' });
  const [registered, setRegistered] = useState(false);
  const [regError, setRegError] = useState('');

  // Wheel
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [hasSpun, setHasSpun] = useState(false);
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const rafRef = useRef(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/gewinnspiel/status`)
      .then(r => r.json()).then(d => setGameActive(d.active))
      .catch(() => setGameActive(false));
  }, []);

  const resetForNextPlayer = () => {
    setForm({ first_name: '', last_name: '', email: '' });
    setRegistered(false);
    setRegError('');
    setSpinning(false);
    setHasSpun(false);
    setResult(null);
    setShowResult(false);
    setRotation(0);
    setSegments(buildSegments()); // Fresh wheel for next player
  };

  const handleRegister = (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      setRegError(t('gw.fill_all')); return;
    }
    setRegError('');
    setRegistered(true);
  };

  const spin = useCallback(async () => {
    if (spinning || hasSpun) return;
    const winIdx = Math.floor(Math.random() * 20);
    const winSeg = segments[winIdx];
    const currentRot = rotation % 360;
    const targetSegCenter = winIdx * SEG_ANGLE + SEG_ANGLE / 2;
    // Fewer rotations (3-5) + much longer duration = slower, more dramatic
    const extraSpins = (6 + Math.floor(Math.random() * 4)) * 360; // faster start
    let delta = (360 - targetSegCenter - currentRot + 360) % 360;
    if (delta < 10) delta += 360;
    const targetRot = rotation + extraSpins + delta;
    setSpinning(true);
    const startRot = rotation;
    const startTime = performance.now();
    const duration = 12000 + Math.random() * 4000; // 12–16 seconds (double)
    const easeOut = t2 => 1 - Math.pow(1 - t2, 4);

    // Start spin sound
    const spinSoundCtx = createSpinSound(duration);

    const animate = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      setRotation(startRot + (targetRot - startRot) * easeOut(progress));
      if (progress < 1) { rafRef.current = requestAnimationFrame(animate); return; }
      setRotation(targetRot);
      setSpinning(false); setHasSpun(true);
      const res = { value: winSeg.value, isJackpot: winSeg.special };
      setResult(res);
      fetch(`${BACKEND_URL}/api/gewinnspiel/play`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: form.first_name, last_name: form.last_name, email: form.email, result_value: winSeg.value, is_jackpot: winSeg.special }),
      }).catch(() => {});
      setTimeout(() => { setShowResult(true); launchFireworks(winSeg.special); }, 400);
    };
    rafRef.current = requestAnimationFrame(animate);
  }, [spinning, hasSpun, rotation, segments, form]);

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#05080F',
      color: '#F5E6D3',
      fontFamily: 'Montserrat,sans-serif',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Caribbean beach background */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 0,
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_spa-magic-portal/artifacts/w9k3vexk_sunset%20palms%20v2.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        filter: 'brightness(0.32) saturate(1.1)',
      }} />
      {/* Dark overlay for readability */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 0,
        background: 'linear-gradient(to bottom, rgba(5,8,15,0.5) 0%, rgba(5,8,15,0.2) 50%, rgba(5,8,15,0.55) 100%)',
      }} />
      {/* Top nav */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, background: 'rgba(5,8,15,0.95)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(212,175,55,0.15)', padding: '10px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Hidden audio element */}
        <audio ref={musicRef} src="/legend-sound.mp3" preload="metadata" loop={true} onEnded={() => setMusicPlaying(false)} />

        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
          <img src={logoImg} alt="Logo" style={{ width: '42px', height: '42px', objectFit: 'contain' }} />
          <div>
            <p style={{ fontFamily: 'Cormorant Garamond,serif', color: '#D4AF37', margin: 0, fontSize: '0.95rem', fontWeight: 700 }}>Legend Spa Hotel</p>
            <p style={{ color: 'rgba(245,230,211,0.45)', margin: 0, fontSize: '0.58rem', letterSpacing: '0.2em' }}>LEGENDS NEVER DIE</p>
          </div>
        </Link>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {/* Legend Sound button — same style as main navbar */}
          <button
            onClick={toggleMusic}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '7px',
              padding: '8px 12px',
              background: 'rgba(5,8,15,0.85)',
              border: '1px solid rgba(212,175,55,0.45)',
              borderRadius: '50px',
              color: '#D4AF37',
              fontFamily: 'Montserrat,sans-serif',
              fontSize: '0.75rem', fontWeight: 600,
              letterSpacing: '0.08em',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              animation: 'goldPulse 2.8s ease-in-out infinite',
            }}
            aria-label="Toggle Legend Sound"
          >
            <span style={{ fontSize: '14px' }}>♪</span>
            <span>{t('hero.legend_sound')}</span>
            {musicPlaying && (
              <span style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '12px' }}>
                {[5, 10, 7].map((h, i) => (
                  <span key={i} style={{
                    width: '2px', background: '#D4AF37', borderRadius: '1px', display: 'block',
                    height: `${h}px`,
                    animation: `soundBar 0.7s ease-in-out ${i * 0.15}s infinite alternate`,
                  }} />
                ))}
              </span>
            )}
          </button>

          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'rgba(245,230,211,0.55)', fontSize: '0.78rem', textDecoration: 'none' }}>
            <ArrowLeft size={13} /> {t('gw.back')}
          </Link>
          <button onClick={() => setShowAdmin(true)} style={btnStyle('rgba(212,175,55,0.1)', '#D4AF37')}>
            <Trophy size={13} /> {t('gw.admin_btn')}
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ paddingTop: '80px', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '96px 20px 48px', position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <p style={{ fontSize: '0.72rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#D4AF37', marginBottom: '6px' }}>{t('gw.tagline')}</p>
          <h1 style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 'clamp(1.8rem, 4vw, 3.2rem)', color: '#F5E6D3', margin: '0 0 6px', lineHeight: 1.1 }}>{t('gw.title')}</h1>
          <p style={{ color: 'rgba(245,230,211,0.5)', maxWidth: '480px', margin: '0 auto', fontSize: '0.88rem' }}>{t('gw.subtitle')}</p>
        </div>

        {gameActive === null && <p style={{ color: 'rgba(245,230,211,0.4)' }}>{t('gw.loading')}</p>}

        {gameActive === false && (
          <div style={{ textAlign: 'center', padding: '40px 32px', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '16px', maxWidth: '400px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '14px' }}>🔒</div>
            <h2 style={{ fontFamily: 'Cormorant Garamond,serif', color: '#D4AF37', marginBottom: '10px' }}>{t('gw.game_inactive_title')}</h2>
            <p style={{ color: 'rgba(245,230,211,0.55)', fontSize: '0.88rem' }}>{t('gw.game_inactive_body')}</p>
          </div>
        )}

        {gameActive && !registered && (
          <div style={{ width: '100%', maxWidth: '400px' }}>
            <div style={{ background: '#070B14', border: '1px solid rgba(212,175,55,0.22)', borderRadius: '16px', padding: '28px 24px' }}>
              <h2 style={{ fontFamily: 'Cormorant Garamond,serif', color: '#D4AF37', marginBottom: '6px', fontSize: '1.35rem' }}>{t('gw.register_title')}</h2>
              <p style={{ color: 'rgba(245,230,211,0.45)', fontSize: '0.83rem', marginBottom: '20px' }}>{t('gw.register_subtitle')}</p>
              <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(245,230,211,0.45)', display: 'block', marginBottom: '5px' }}>{t('gw.first_name_label')} *</label>
                    <input style={inputStyle} value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))} required />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(245,230,211,0.45)', display: 'block', marginBottom: '5px' }}>{t('gw.last_name_label')} *</label>
                    <input style={inputStyle} value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))} required />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(245,230,211,0.45)', display: 'block', marginBottom: '5px' }}>{t('gw.email_label2')} *</label>
                  <input style={inputStyle} type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required />
                </div>
                {regError && <p style={{ color: '#E45D5D', fontSize: '0.83rem', margin: 0 }}>{regError}</p>}
                <button type="submit" style={{ ...btnStyle('#D4AF37', '#05080F'), justifyContent: 'center', padding: '13px', fontSize: '0.88rem', borderRadius: '10px' }}>
                  <Star size={15} /> {t('gw.btn_to_wheel')}
                </button>
              </form>
              <p style={{ color: 'rgba(245,230,211,0.3)', fontSize: '0.73rem', marginTop: '14px', textAlign: 'center' }}>{t('gw.one_email_note')}</p>
            </div>
          </div>
        )}

        {gameActive && registered && !showResult && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '22px' }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: '#D4AF37', fontFamily: 'Cormorant Garamond,serif', fontSize: '1.05rem', margin: 0 }}>{t('gw.hello')} {form.first_name}! 🎉</p>
              <p style={{ color: 'rgba(245,230,211,0.45)', fontSize: '0.82rem', margin: '4px 0 0' }}>
                {hasSpun ? t('gw.already_spun') : t('gw.register_subtitle')}
              </p>
            </div>
            <LuckyWheel rotation={rotation} spinning={spinning} segments={segments} />
            {!hasSpun && (
              <button onClick={spin} disabled={spinning} style={{
                padding: '15px 48px', background: spinning ? 'rgba(212,175,55,0.3)' : '#D4AF37',
                border: 'none', borderRadius: '50px', color: '#05080F',
                fontFamily: 'Montserrat,sans-serif', fontSize: '1rem', fontWeight: 700,
                letterSpacing: '0.14em', textTransform: 'uppercase',
                cursor: spinning ? 'not-allowed' : 'pointer',
                boxShadow: spinning ? 'none' : '0 0 28px rgba(212,175,55,0.4)',
                animation: spinning ? 'none' : 'goldPulse 2.5s ease-in-out infinite',
              }} data-testid="spin-button">
                {spinning ? t('gw.btn_spinning') : t('gw.btn_spin')}
              </button>
            )}
          </div>
        )}

        {/* Result overlay with Hotel background */}
        {showResult && result && (
          <div style={{
            position: 'fixed', inset: 0,
            zIndex: 500,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column', padding: '24px', textAlign: 'center',
            overflow: 'hidden',
          }}>
            {/* Hotel background image */}
            <div style={{
              position: 'absolute', inset: 0,
              backgroundImage: 'url(https://customer-assets.emergentagent.com/job_spa-magic-portal/artifacts/f0622fzt_Hotel.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center center',
              filter: 'brightness(0.45) saturate(1.1)',
              zIndex: 0,
            }} />
            {/* Dark gradient overlay for text readability */}
            <div style={{
              position: 'absolute', inset: 0,
              background: 'radial-gradient(ellipse at center, rgba(5,8,15,0.4) 0%, rgba(5,8,15,0.75) 100%)',
              zIndex: 0,
            }} />

            {/* Result content — glass card for perfect readability */}
            <div style={{
              position: 'relative', zIndex: 2,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              background: 'rgba(5, 8, 15, 0.72)',
              border: '1px solid rgba(212,175,55,0.35)',
              borderRadius: '24px',
              padding: 'clamp(28px, 5vw, 52px) clamp(28px, 6vw, 64px)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              boxShadow: '0 8px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(212,175,55,0.1)',
              maxWidth: '520px',
              width: '100%',
            }}>
              {result.isJackpot ? (
                <>
                  <div style={{ fontSize: 'clamp(3rem, 10vw, 6rem)', marginBottom: '12px', animation: 'spinStar 0.8s ease-out' }}>🌟</div>
                  <h1 style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 'clamp(2.2rem, 7vw, 5rem)', color: '#FFD700', marginBottom: '6px', textShadow: '0 2px 20px rgba(0,0,0,0.8)', lineHeight: 1.1 }}>
                    {t('gw.jackpot_title')}
                  </h1>
                  <p style={{ fontSize: 'clamp(1.6rem, 5vw, 2.8rem)', color: '#FFD700', fontWeight: 700, marginBottom: '16px', textShadow: '0 2px 16px rgba(0,0,0,0.8)' }}>500 $LSPA Token</p>
                  <p style={{ color: '#F5E6D3', fontSize: '0.95rem', maxWidth: '380px', marginBottom: '8px', textShadow: '0 1px 8px rgba(0,0,0,0.9)' }}>{t('gw.jackpot_body')}</p>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 'clamp(2.5rem, 8vw, 5rem)', marginBottom: '12px' }}>🎉</div>
                  <p style={{ color: '#D4AF37', fontSize: '0.78rem', letterSpacing: '0.28em', textTransform: 'uppercase', marginBottom: '8px' }}>
                    {t('gw.result_congrats')}, {form.first_name}!
                  </p>
                  <h1 style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 'clamp(4rem, 12vw, 8rem)', color: '#FFD700', margin: 0, lineHeight: 1, textShadow: '0 2px 24px rgba(0,0,0,0.9)' }}>
                    {result.value}
                  </h1>
                  <p style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 'clamp(1.4rem, 3vw, 2rem)', color: '#D4AF37', marginBottom: '16px', textShadow: '0 1px 12px rgba(0,0,0,0.8)' }}>{t('gw.result_token')}</p>
                  <p style={{ color: '#F5E6D3', fontSize: '0.88rem', maxWidth: '340px', marginBottom: '8px' }}>{t('gw.result_body')}</p>
                </>
              )}
              <p style={{ color: 'rgba(245,230,211,0.5)', fontSize: '0.73rem', marginBottom: '24px' }}>
                {t('gw.result_registered')} {form.email}
              </p>

              {/* Next Player Button */}
              <button
                onClick={resetForNextPlayer}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '10px',
                  padding: '14px 36px',
                  background: 'rgba(5,8,15,0.7)',
                  border: '2px solid rgba(212,175,55,0.6)',
                  borderRadius: '50px',
                  color: '#D4AF37', fontFamily: 'Montserrat,sans-serif',
                  fontSize: '0.92rem', fontWeight: 700, letterSpacing: '0.1em',
                  cursor: 'pointer',
                  backdropFilter: 'blur(16px)',
                  boxShadow: '0 0 28px rgba(212,175,55,0.2)',
                  transition: 'all 0.25s ease',
                  animation: 'fadeInUp 0.5s 0.5s ease both',
                }}
                data-testid="next-player-btn"
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,175,55,0.18)'; e.currentTarget.style.boxShadow = '0 0 40px rgba(212,175,55,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(5,8,15,0.8)'; e.currentTarget.style.boxShadow = '0 0 28px rgba(212,175,55,0.2)'; }}
              >
                ✦ {t('gw.btn_next_player')}
              </button>
            </div>
          </div>
        )}
      </div>

      {showAdmin && <AdminDashboard onClose={() => setShowAdmin(false)} />}

      <style>{`
        @keyframes spinStar { from { transform: scale(0) rotate(-180deg); } to { transform: scale(1) rotate(0deg); } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes goldPulse {
          0%, 100% { box-shadow: 0 0 6px rgba(212,175,55,0.25), 0 0 12px rgba(212,175,55,0.1); }
          50% { box-shadow: 0 0 14px rgba(212,175,55,0.6), 0 0 28px rgba(212,175,55,0.25); }
        }
        @keyframes goldBorderSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes soundBar { from { height: 3px; opacity: 0.5; } to { height: 11px; opacity: 1; } }
      `}</style>
    </div>
  );
}
